import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { compactFieldSx } from '../Onboarding/fieldStyles';
import ChatLibraryScopeToggle from './ChatLibraryScopeToggle';
import {
  applyChatLibraryFilters,
  CHAT_LIBRARY_SCOPE_ALL,
  CHAT_LIBRARY_SCOPE_RECENT,
  CHAT_LIBRARY_SORT_NEWEST,
  CHAT_LIBRARY_SORT_OLDEST,
  chatSessionCountLabel,
  formattedSessionDate,
  sessionTitle,
} from './chatSessionListUtils';
import {
  CHAT_LIBRARY_COUNT_SX,
  CHAT_LIBRARY_EMPTY_BODY_SX,
  CHAT_LIBRARY_EMPTY_SX,
  CHAT_LIBRARY_EMPTY_TITLE_SX,
  CHAT_LIBRARY_HEADER_ACTIONS_SX,
  CHAT_LIBRARY_HEADER_SX,
  CHAT_LIBRARY_LIST_HEADER_LABEL_SX,
  CHAT_LIBRARY_LIST_HEADER_SX,
  CHAT_LIBRARY_PAGE_TITLE_SX,
  CHAT_LIBRARY_ROW_DATE_SX,
  CHAT_LIBRARY_ROW_MAIN_SX,
  CHAT_LIBRARY_ROW_PREVIEW_SX,
  CHAT_LIBRARY_ROW_TITLE_SX,
  CHAT_LIBRARY_SEARCH_SX,
  CHAT_LIBRARY_SORT_SX,
  CHAT_LIBRARY_TOOLBAR_SX,
  chatLibraryRowSx,
} from './chatLibraryStyles';
import { CHAT_INDEX_CANVAS_SX, CHAT_LIBRARY_LANE_SX } from './chatStyles';
import { CHAT_CONNECTION_ERROR_MESSAGE } from './chatConnectionError';

/**
 * Conversation history management for the V2/V3 chat shell.
 */
export default function ChatLibraryView({
  sessions = [],
  loading = false,
  error = null,
  selectedSessionId = null,
  onRetry,
  onNewChat,
  onOpenSession,
}) {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState(CHAT_LIBRARY_SCOPE_ALL);
  const [sortOrder, setSortOrder] = useState(CHAT_LIBRARY_SORT_NEWEST);

  const filteredSessions = useMemo(
    () => applyChatLibraryFilters(sessions, { query: search, scope, sortOrder }),
    [sessions, search, scope, sortOrder]
  );

  const trimmedSearch = search.trim();
  const showLimitLabel = !loading && !error && sessions.length === 50;
  const hasSessions = sessions.length > 0;
  const hasFilteredSessions = filteredSessions.length > 0;

  const handleOpen = (sessionId) => {
    onOpenSession?.(sessionId);
  };

  const emptyLibraryCopy = 'Start a new conversation to build your chat library.';
  const emptyRecentCopy = 'No chats from the last two weeks.';
  const noResultsCopy = trimmedSearch
    ? 'No chats match your search.'
    : (scope === CHAT_LIBRARY_SCOPE_RECENT ? emptyRecentCopy : emptyLibraryCopy);

  return (
    <Box data-testid="chat-library-view" sx={CHAT_INDEX_CANVAS_SX}>
      <Box sx={CHAT_LIBRARY_LANE_SX}>
        <Box sx={CHAT_LIBRARY_HEADER_SX}>
          <Typography component="h1" sx={CHAT_LIBRARY_PAGE_TITLE_SX}>
            Chat Library
          </Typography>
          <Box sx={CHAT_LIBRARY_HEADER_ACTIONS_SX}>
            <TextField
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search chats…"
              inputProps={{ 'aria-label': 'Search chats', style: { fontSize: 13 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...compactFieldSx, ...CHAT_LIBRARY_SEARCH_SX }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddCommentOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={onNewChat}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                minHeight: 36,
                px: 1.75,
                boxShadow: 'none',
                flexShrink: 0,
              }}
            >
              New Chat
            </Button>
          </Box>
        </Box>

        {!loading && !error && hasSessions ? (
          <Box sx={CHAT_LIBRARY_TOOLBAR_SX} data-testid="chat-library-toolbar">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <ChatLibraryScopeToggle value={scope} onChange={setScope} />
              <FormControl size="small" sx={CHAT_LIBRARY_SORT_SX}>
                <Select
                  value={sortOrder}
                  onChange={(event) => setSortOrder(event.target.value)}
                  inputProps={{ 'aria-label': 'Sort chats' }}
                  data-testid="chat-library-sort"
                  sx={compactFieldSx}
                >
                  <MenuItem value={CHAT_LIBRARY_SORT_NEWEST}>Newest first</MenuItem>
                  <MenuItem value={CHAT_LIBRARY_SORT_OLDEST}>Oldest first</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {showLimitLabel ? (
              <Typography sx={CHAT_LIBRARY_COUNT_SX}>
                {chatSessionCountLabel(sessions.length, 50)}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {loading ? [0, 1, 2, 3, 4].map((item) => (
          <Skeleton key={item} variant="rounded" height={64} sx={{ borderRadius: 1 }} />
        )) : null}

        {!loading && error ? (
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" onClick={onRetry} sx={{ fontSize: 12 }}>
                Retry
              </Button>
            )}
          >
            {error === CHAT_CONNECTION_ERROR_MESSAGE ? error : 'Unable to load chats.'}
          </Alert>
        ) : null}

        {!loading && !error && !hasSessions ? (
          <Box sx={CHAT_LIBRARY_EMPTY_SX} data-testid="chat-library-empty">
            <Typography sx={CHAT_LIBRARY_EMPTY_TITLE_SX}>
              No chats yet
            </Typography>
            <Typography sx={CHAT_LIBRARY_EMPTY_BODY_SX}>
              {emptyLibraryCopy}
            </Typography>
            <Button
              onClick={onNewChat}
              sx={{ mt: 1.25, textTransform: 'none', fontSize: 13, fontWeight: 600 }}
            >
              New Chat
            </Button>
          </Box>
        ) : null}

        {!loading && !error && hasSessions ? (
          <>
            {!hasFilteredSessions ? (
              <Box sx={CHAT_LIBRARY_EMPTY_SX} data-testid="chat-library-no-results">
                <Typography sx={CHAT_LIBRARY_EMPTY_BODY_SX}>
                  {noResultsCopy}
                </Typography>
                {trimmedSearch ? (
                  <Button
                    onClick={() => setSearch('')}
                    sx={{ textTransform: 'none', mt: 1, fontSize: 13, fontWeight: 600 }}
                  >
                    Clear search
                  </Button>
                ) : null}
              </Box>
            ) : (
              <Box data-testid="chat-library-list">
                <Box sx={CHAT_LIBRARY_LIST_HEADER_SX} aria-hidden="true">
                  <Typography sx={CHAT_LIBRARY_LIST_HEADER_LABEL_SX}>
                    Conversation
                  </Typography>
                  <Typography sx={{ ...CHAT_LIBRARY_LIST_HEADER_LABEL_SX, minWidth: 148, textAlign: 'right' }}>
                    Modified
                  </Typography>
                </Box>
                {filteredSessions.map((session) => {
                  const title = sessionTitle(session);
                  const date = formattedSessionDate(session);
                  const selected = session.session_id === selectedSessionId;
                  return (
                    <Tooltip key={session.session_id} title={title} placement="top-start">
                      <Box
                        component="button"
                        type="button"
                        onClick={() => handleOpen(session.session_id)}
                        aria-label={`Open chat: ${title}`}
                        aria-current={selected ? 'true' : undefined}
                        sx={chatLibraryRowSx(theme, { selected })}
                      >
                        <Box sx={CHAT_LIBRARY_ROW_MAIN_SX}>
                          <Typography noWrap sx={CHAT_LIBRARY_ROW_TITLE_SX}>
                            {title}
                          </Typography>
                          {session.last_message_preview ? (
                            <Typography noWrap sx={CHAT_LIBRARY_ROW_PREVIEW_SX}>
                              {session.last_message_preview}
                            </Typography>
                          ) : null}
                        </Box>
                        {date ? (
                          <Typography sx={CHAT_LIBRARY_ROW_DATE_SX}>
                            {date}
                          </Typography>
                        ) : null}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            )}
          </>
        ) : null}
      </Box>
    </Box>
  );
}
